import { useState } from 'react';
import { ChevronDown, ChevronRight, CornerDownLeft, X } from 'lucide-react';
import clsx from 'clsx';
import type { WatcherSnapshot, WatchHit } from '@/lib/log-watch/types';

/**
 * 单 key 的展示行（M2 简化版）。
 *   - 顶部：键名（用户输入的原值，含中文）+ 命中次数 + 删除按钮；
 *   - 中部：最新一条命中（点击跳到原日志行）；
 *   - 折叠箭头：多条历史时展开，按时间倒序列出旧值；
 *   - 无命中态：显示「尚未命中，等待匹配 key=value / key: value」。
 */

interface Props {
  /** 用户输入的原始键名（用作展示和 onRemove 回调）。 */
  keyName: string;
  /** 引擎的 watcher snapshot；keys 列表存在但 runner 还没启动时可为空。 */
  snapshot?: WatcherSnapshot;
  onJumpToLine?: (lineId: number) => void;
  onRemove: (keyName: string) => void;
}

export function KeyRow({ keyName, snapshot, onJumpToLine, onRemove }: Props): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const count = snapshot?.count ?? 0;
  const has = count > 0;
  const tone = has
    ? 'border-emerald-500/40 bg-emerald-500/5'
    : 'border-line bg-bg-card';
  return (
    <li className={clsx('rounded-md border px-2 py-1.5 text-[11px]', tone)}>
      <div className="flex items-center gap-1.5">
        {snapshot && snapshot.history.length > 1 ? (
          <button
            type="button"
            className="text-ink-muted hover:text-ink"
            onClick={() => setExpanded((v) => !v)}
            title={expanded ? '折叠历史值' : '展开历史值'}
          >
            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>
        ) : (
          <span className="inline-block h-3 w-3" />
        )}
        <span className="font-mono text-ink" title={keyName}>
          {keyName}
        </span>
        <span className="ml-auto inline-flex items-center gap-2 text-ink-muted">
          <span className="font-mono tabular-nums">× {count}</span>
          <button
            type="button"
            className="rounded text-ink-muted hover:bg-bg-soft hover:text-rose-300"
            onClick={() => onRemove(keyName)}
            title="移除该 key"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      </div>
      {snapshot?.latest ? (
        <div className="mt-1 ml-[18px]">
          <HitRow hit={snapshot.latest} onJump={onJumpToLine} />
        </div>
      ) : (
        <p className="mt-1 ml-[18px] text-[10px] text-ink-dim">
          尚未命中，等待匹配 <code className="font-mono">{keyName}=值</code> /{' '}
          <code className="font-mono">{keyName}: 值</code>
        </p>
      )}
      {expanded && snapshot && snapshot.history.length > 1 && (
        <ul className="mt-1 ml-[18px] space-y-1 border-l border-line pl-2 text-ink-muted">
          {snapshot.history
            .slice(0, snapshot.history.length - 1)
            .reverse()
            .map((h, idx) => (
              <li key={`${h.lineId}-${idx}`}>
                <HitRow hit={h} onJump={onJumpToLine} faded />
              </li>
            ))}
        </ul>
      )}
    </li>
  );
}

interface HitRowProps {
  hit: WatchHit;
  onJump?: (lineId: number) => void;
  faded?: boolean;
}

function HitRow({ hit, onJump, faded }: HitRowProps): JSX.Element {
  const meta = [
    hit.ts,
    hit.pid ? `pid ${hit.pid}` : null,
    hit.level && hit.level !== '?' ? hit.level : null,
    hit.tag,
  ]
    .filter(Boolean)
    .join(' · ');
  const valueClass = clsx(
    'block break-all font-mono text-[11px]',
    faded ? 'text-ink-muted' : 'text-ink',
  );
  return (
    <div>
      <button
        type="button"
        className="group flex w-full items-start gap-1 text-left"
        onClick={() => onJump?.(hit.lineId)}
        title={`跳到日志行 #${hit.lineId}`}
      >
        <CornerDownLeft className="mt-[2px] h-3 w-3 shrink-0 text-ink-dim group-hover:text-ink" />
        <span className={valueClass}>{hit.value || '(空值)'}</span>
      </button>
      {meta && <div className="mt-0.5 text-[10px] text-ink-dim">{meta}</div>}
    </div>
  );
}
